import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { emailService } from '../../../services/emailService';

const EmergencyResources = () => {
  const navigate = useNavigate();
  const [emailSent, setEmailSent] = React.useState(false);

  // Send crisis resources email when user views this component
  React.useEffect(() => {
    const sendEmail = async () => {
      if (!emailSent) {
        try {
          const result = await emailService?.sendCrisisResources();
          // Silently handle if Edge Function not deployed or has errors
          if (result?.notDeployed) {
            console.info('Crisis resources email feature requires Edge Function deployment');
          }
          setEmailSent(true);
        } catch (error) {
          // Silently fail - don't disrupt user experience
          console.info('Crisis resources email not available:', error?.message);
          setEmailSent(true);
        }
      }
    };
    sendEmail();
  }, [emailSent]);

  const emergencyContacts = [
    {
      id: 1,
      name: "National Suicide Prevention Lifeline",
      number: "XXX",
      description: "24/7 crisis support for anyone in emotional distress or suicidal crisis",
      icon: "Phone",
      type: "call"
    },
    {
      id: 2,
      name: "Crisis Text Line",
      number: "XXXXXX",
      description: "Text HOME to connect with a crisis counselor",
      icon: "MessageSquare",
      type: "text"
    },
    {
      id: 3,
      name: "Emergency Services",
      number: "XXX 000 911",
      description: "For immediate life-threatening emergencies",
      icon: "AlertTriangle",
      type: "call"
    }
  ];

  const localResources = [
    {
      id: 1,
      name: "Community Mental Health Center",
      address: "123 Wellness Street, Building A",
      phone: "1800 xxx xxz",
      hours: "Mon-Fri: 8:00 AM - 8:00 PM",
      icon: "Building"
    },
    {
      id: 2,
      name: "24/7 Crisis Walk-In Center",
      address: "456 Support Avenue, Suite 200",
      phone: "1800 xxx xxy",
      hours: "Open 24/7",
      icon: "MapPin"
    },
    {
      id: 3,
      name: "Employee Assistance Program",
      address: "Available remotely",
      phone: "1800 xxx xxx",
      hours: "24/7 Phone Support",
      icon: "Headphones"
    }
  ];

  const handleEmergencyCall = (number) => {
    window.location.href = `tel:${number}`;
  };

  const handleEmergencyText = (number) => {
    window.location.href = `sms:${number}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
          <Icon name="AlertCircle" size={24} className="text-error" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Emergency Resources</h2>
          <p className="text-sm text-muted-foreground">Available 24/7 for immediate support</p>
        </div>
      </div>
      {/* Crisis Hotlines */}
      <div className="card-elevated p-6 border-l-4 border-error">
        <h3 className="text-lg font-semibold text-foreground mb-4">Crisis Hotlines</h3>
        <div className="space-y-4">
          {emergencyContacts?.map((contact) => (
            <div key={contact?.id} className="flex items-start gap-4 p-4 rounded-lg bg-muted hover:bg-muted/70 transition-colors duration-200">
              <div className="w-12 h-12 rounded-lg bg-error/10 flex items-center justify-center flex-shrink-0">
                <Icon name={contact?.icon} size={24} className="text-error" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-semibold text-foreground mb-1">{contact?.name}</h4>
                <p className="text-sm text-muted-foreground mb-3">{contact?.description}</p>
                <Button
                  variant="destructive"
                  iconName={contact?.type === 'call' ? 'Phone' : 'MessageSquare'}
                  iconPosition="left"
                  onClick={() => contact?.type === 'call' ? handleEmergencyCall(contact?.number) : handleEmergencyText(contact?.number)}
                >
                  {contact?.type === 'call' ? 'Call' : 'Text'} {contact?.number}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Local Resources */}
      <div className="card-elevated p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Local Support Centers</h3>
        <div className="space-y-4">
          {localResources?.map((resource) => (
            <div key={resource?.id} className="flex items-start gap-4 p-4 rounded-lg border border-border hover:border-primary transition-colors duration-200">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={resource?.icon} size={24} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-semibold text-foreground mb-2">{resource?.name}</h4>
                <div className="space-y-1 mb-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="MapPin" size={14} />
                    <span>{resource?.address}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Phone" size={14} />
                    <span>{resource?.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Clock" size={14} />
                    <span>{resource?.hours}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Phone"
                    iconPosition="left"
                    onClick={() => handleEmergencyCall(resource?.phone)}
                  >
                    Call
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Navigation"
                    iconPosition="left"
                    onClick={() => {
                      const address = encodeURIComponent(resource?.address);
                      window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
                    }}
                  >
                    Get Directions
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Safety Plan */}
      <div className="card-elevated p-6 bg-primary/5">
        <div className="flex items-start gap-3 mb-4">
          <Icon name="Shield" size={24} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Personal Safety Plan</h3>
            <p className="text-sm text-muted-foreground">Create a plan for managing difficult moments</p>
          </div>
        </div>
        <Button
          variant="outline"
          iconName="FileText"
          iconPosition="left"
          fullWidth
          onClick={() => console.log('Create Safety Plan - Feature coming soon')}
        >
          Create Safety Plan
        </Button>
      </div>
    </div>
  );
};

export default EmergencyResources;